import { Button, Card, Field, Input, TextChips, useTheme } from '@chessmate/ui';
import { useEffect, useRef, useState } from 'react';
import { ParentSupport } from '../components/ParentSupport.js';
import { PageHeader, PANEL_CLASS } from '../components/PageHeader.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { useT } from '../i18n/useT.js';
import { LoadedImage } from '../components/LoadedImage.js';
import { type KidProfile, type ParentPrefs, api } from '../services/api.js';
import { useParentStore } from '../state/parent-store.js';

/** Debounce window for pushing local appearance changes to the server. */
const PREFS_PUSH_DEBOUNCE_MS = 600;

export interface ParentDashboardProps {
  readonly onExit: () => void;
}

const COACH_LEVELS = ['gentle', 'balanced', 'advanced'] as const;

export function ParentDashboard({ onExit }: ParentDashboardProps) {
  const { t } = useT('common');
  const { t: tNav } = useT('navigation');
  const session = useParentStore((s) => s.session);
  const setSession = useParentStore((s) => s.setSession);
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kids, setKids] = useState<KidProfile[]>([]);
  const [nickname, setNickname] = useState('');
  const [playerToken, setPlayerToken] = useState('');
  const [adding, setAdding] = useState(false);
  const { theme, accent, setTheme, setAccent } = useTheme();
  // Last prefs value pushed or pulled from the server — prevents an echo
  // loop where applying fetched prefs re-triggers the push effect.
  const syncedPrefs = useRef<string>('');

  const auth = async () => {
    if (email.trim().length === 0 || password.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const created =
        mode === 'signup'
          ? await api.parentSignup(email.trim(), password)
          : await api.parentLogin(email.trim(), password);
      setSession({ parentId: created.parentId, email: created.email, token: created.token });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  // Pull server prefs after sign-in (and on revisit with a live session):
  // server wins over local, since it reflects the latest change on ANY device.
  useEffect(() => {
    if (!session) return;
    api
      .getParentPrefs(session.token)
      .then((prefs: ParentPrefs) => {
        if (prefs.theme && prefs.theme !== theme) setTheme(prefs.theme);
        if (prefs.accent && prefs.accent !== accent) setAccent(prefs.accent);
        syncedPrefs.current = JSON.stringify(prefs);
      })
      .catch(() => {
        // Prefs are cosmetic — a failed pull must not block the dashboard.
      });
  }, [session?.token]);

  // Push local appearance changes to the server while signed in (debounced),
  // so this device's choice follows the parent to every other device.
  useEffect(() => {
    if (!session) return;
    const next: ParentPrefs = { theme, accent };
    if (JSON.stringify(next) === syncedPrefs.current) return;
    const timer = setTimeout(() => {
      syncedPrefs.current = JSON.stringify(next);
      api.updateParentPrefs(session.token, next).catch(() => {});
    }, PREFS_PUSH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [session?.token, theme, accent]);

  useEffect(() => {
    if (!session) return;
    api
      .fetchParentKids(session.token)
      .then(setKids)
      .catch(() => setError('Could not load kids.'));
  }, [session]);

  const addKid = async () => {
    if (!session || nickname.trim().length === 0 || playerToken.trim().length === 0) return;
    setAdding(true);
    setError(null);
    try {
      const kid = await api.addKid(session.token, {
        nickname: nickname.trim(),
        playerToken: playerToken.trim(),
      });
      setKids((prev) => [...prev, kid]);
      setNickname('');
      setPlayerToken('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the kid.');
    } finally {
      setAdding(false);
    }
  };

  const updatePrefs = async (kidId: string, prefs: Parameters<typeof api.updateKidPrefs>[2]) => {
    if (!session) return;
    setError(null);
    try {
      const kid = await api.updateKidPrefs(session.token, kidId, prefs);
      setKids((prev) => prev.map((k) => (k.id === kidId ? kid : k)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save changes.');
    }
  };

  const signOut = () => {
    if (session) api.parentLogout(session.token).catch(() => {});
    setSession(null);
    setKids([]);
  };

  if (!session) {
    return (
      <ScreenLayout center width="wide" data-testid="parent-dashboard" data-appdom-scope="parent">
        <div className={`${PANEL_CLASS} grid w-full max-w-5xl overflow-hidden lg:grid-cols-2`}>
          <LoadedImage src="/art/spot-parent.webp" alt={t('art-alt-parent')} data-testid="parent-login-art" className="h-40 w-full object-cover sm:h-52 lg:h-full lg:min-h-[420px]" />
          <div className="flex flex-col justify-center gap-5 p-6 sm:p-10 lg:p-12">
            <h1 className="font-display text-h1 font-bold text-fg lg:text-5xl">Parents</h1>
            <Field
              id="parent-email"
              inputProps={{
                'data-testid': 'parent-email',
                type: 'email',
                value: email,
                onChange: (e) => setEmail(e.target.value),
                placeholder: 'you@example.com',
                autoComplete: 'email',
              }}
              label="Email"
            />
            <Field
              id="parent-password"
              inputProps={{
                'data-testid': 'parent-password',
                type: 'password',
                value: password,
                onChange: (e) => setPassword(e.target.value),
                placeholder: '••••••••',
                autoComplete: mode === 'signup' ? 'new-password' : 'current-password',
              }}
              label="Password"
            />
            <Button
              data-testid="parent-submit"
              data-appdom-id="parent-submit"
              data-appdom-type="Button"
              loading={busy}
              onClick={() => void auth()}
              disabled={email.trim().length === 0 || password.length === 0}
            >
              {busy ? t('working') : mode === 'signup' ? t('create-account') : t('sign-in')}
            </Button>
            <Button
              variant="link"
              size="sm"
              data-testid="parent-toggle-mode"
              data-appdom-id="parent-toggle-mode"
              data-appdom-type="Button"
              className="self-center"
              onClick={() => setMode((m) => (m === 'login' ? 'signup' : 'login'))}
            >
              {mode === 'signup' ? t('already-have-account') : t('no-account-yet')}
            </Button>
          </div>
        </div>
        {error && (
          <p className={`${PANEL_CLASS} px-4 py-2 font-bold text-red-700 dark:text-red-300`} data-testid="parent-error">
            {error}
          </p>
        )}
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout width="wide" data-testid="parent-dashboard" data-appdom-scope="parent">
      <PageHeader
        title={tNav('parents')}
        spot={{ src: '/art/spot-parent.webp', alt: t('art-alt-parent') }}
        subtitle={<span data-testid="parent-email-label">{session.email}</span>}
        action={
          <div className="flex flex-wrap gap-2">
            <ParentSupport token={session.token} />
            <Button
              variant="secondary"
              data-testid="parent-sign-out"
              data-appdom-id="parent-sign-out"
              data-appdom-type="Button"
              onClick={signOut}
            >
              {t('sign-out')}
            </Button>
            <Button
              variant="secondary"
              data-testid="parent-exit"
              data-appdom-id="parent-exit"
              data-appdom-type="Button"
              onClick={onExit}
            >
              {t('back')}
            </Button>
          </div>
        }
      />
      {error && (
        <p className={`${PANEL_CLASS} px-4 py-2 font-bold text-red-700 dark:text-red-300`} data-testid="parent-error">
          {error}
        </p>
      )}
      <div className="grid items-start gap-6 lg:grid-cols-5 lg:gap-8">
      <Card title="Add a kid" className={`${PANEL_CLASS} flex flex-col gap-4 p-5 sm:p-7 lg:col-span-2`}>
        <div className="flex flex-col gap-3">
          <input
            data-testid="kid-nickname"
            aria-label="Nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Kid nickname"
            className="min-h-[48px] rounded-2xl border-2 border-sun/40 bg-white px-4 py-2 text-lg font-semibold text-ink dark:text-cream"
          />
          <input
            data-testid="kid-token"
            aria-label="Player token"
            value={playerToken}
            onChange={(e) => setPlayerToken(e.target.value)}
            placeholder="Paste the player's token"
            className="min-h-[48px] rounded-2xl border-2 border-sun/40 bg-white px-4 py-2 text-lg font-semibold text-ink dark:text-cream"
          />
          <Button
            data-testid="kid-add"
            onClick={() => void addKid()}
            disabled={adding || nickname.trim().length === 0 || playerToken.trim().length === 0}
          >
            {adding ? t('adding') : t('link-kid-profile')}
          </Button>
        </div>
      </Card>

      <Card title="My kids" className={`${PANEL_CLASS} flex flex-col gap-4 p-5 sm:p-7 lg:col-span-3`}>
        {kids.length === 0 ? (
          <p className="text-base font-semibold text-fg-muted">
            No kids linked yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-4">
            {kids.map((kid) => (
              <li
                key={kid.id}
                className="flex flex-col gap-2 rounded-2xl bg-cream/80 p-4 glass glass-light lift"
                data-testid={`kid-row-${kid.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-ink">
                    {kid.nickname} ({kid.playerName})
                  </span>
                  <span
                    className="text-lg font-black text-berry"
                    data-testid={`kid-rating-${kid.id}`}
                  >
                    {kid.rating == null ? '—' : Math.round(kid.rating)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-2">
                    <TextChips value={kid.coachLevel} />
                    <select
                      data-testid={`kid-coach-${kid.id}`}
                      value={kid.coachLevel}
                      onChange={(e) =>
                        void updatePrefs(kid.id, {
                          coachLevel: e.target.value as KidProfile['coachLevel'],
                        })
                      }
                      aria-label="Coach level"
                      className="sr-only"
                    >
                      {COACH_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </span>
                  <label className="flex items-center gap-2 text-base font-semibold text-ink/70">
                    <input
                      data-testid={`kid-screen-${kid.id}`}
                      type="number"
                      min={0}
                      max={720}
                      defaultValue={kid.screenTimeMinutes}
                      onBlur={(e) => {
                        const value = Number(e.target.value);
                        if (
                          Number.isInteger(value) &&
                          value >= 0 &&
                          value <= 720 &&
                          value !== kid.screenTimeMinutes
                        ) {
                          void updatePrefs(kid.id, { screenTimeMinutes: value });
                        }
                      }}
                      className="min-h-[48px] w-24 rounded-2xl border-2 border-sun/40 bg-white px-3 py-1 font-semibold text-ink dark:text-cream"
                    />
                    {t('min-per-day')}
                  </label>
                  <Button
                    variant="ghost"
                    data-testid={`kid-reset-${kid.id}`}
                    onClick={() => void updatePrefs(kid.id, { resetRatings: true })}
                    className="min-h-[48px]"
                  >
                    {t('reset-rating')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      </div>
    </ScreenLayout>
  );
}
